import ProfileImage from "src/components/film/filmwebid/ProfileImage";
import ReviewMenu from "src/components/film/filmwebid/ReviewMenu";
import Review from "src/components/filmwebid/Review";
import UserName from "src/components/filmwebid/UserName";
import HeartSvg from "src/components/svg/HeartSvg";
import theme from "src/components/themes/DefaultTheme";
import { useUserContext } from "src/lib/contexts/UserContext";
import IMovieReview from "src/lib/filmwebid/IMovieReview";
import useReviewLike from "src/lib/filmwebid/useReviewLike";
import styled from 'src/lib/styles/css';
import { useMemo } from "react";



//#region [Props]
type ReviewWithProfileProps = {
	review: IMovieReview;
	isLiked: boolean;
	onTextClick?: () => void;
	isReported?: boolean;
	streamingId?: number;
	edi?: string;
	className?: string;
};
//#endregion

//#region [Component]
export default function ReviewWithProfile({ review, isLiked, onTextClick, isReported = false, streamingId, edi, className }: ReviewWithProfileProps) {
	const authContext = useUserContext();
	const [doLike, isLiking, _isLiked] = useReviewLike(review.reviewId, edi, streamingId, isLiked);
	const numLikes = useMemo(() => {
		let result = review.likes ?? 0;
		if (isLiked && !_isLiked) {
			// the user has un-liked the review
			result -= 1;
		} else if (!isLiked && _isLiked) {
			// the user has liked the review
			result += 1;
		}
		return result;
	}, [isLiked, _isLiked, review.likes]);

	return <SReviewWithProfile className={className}>
		<SReview review={review} onTextClick={onTextClick} />
		<SUserCol>
			<ProfileImage
				userId={review.userId}
				pictureUrl={review.pictureUrl}
				hasUploadedPicture={review.hasUploadedPicture}
				userName={review.userName}
			/>
			<SUserName userName={review.userName} />
			<SLikesContainer>
				{numLikes > 0 && <SNumLikedThis>{numLikes}</SNumLikedThis>}
				{authContext.isMe(review.userId) ?
					<>{numLikes > 0 && <SLikeReviewIcon selected={_isLiked} />}</> :
					<SLikeReviewButton onClick={() => doLike(review.userId)}><SLikeReviewIcon selected={_isLiked} /></SLikeReviewButton>}
			</SLikesContainer>
		</SUserCol>
		{!authContext.isMe(review.userId) && <SReviewMenu isReported={isReported} review={review} />}
	</SReviewWithProfile>;
}
//#endregion

//#region [Styles]
const SReviewWithProfile = styled.div`
	padding: 9px 13px;
	border: 1px solid ${theme.palette.lillaHovedfarge};
	background-color: ${theme.palette.lilla4};
	border-radius: 10px;
	position: relative;
	display: flex;
	flex-direction: column;

	gap: 17px;

	--portrait-size: 44px;
`;

const SReview = styled(Review)`
	flex: 1 1 auto;
	margin-right: 15px;
`;

const SUserCol = styled.div`
	flex: 0 0 auto;
	text-align: center;
	display: flex;
	align-items: center;
	gap: 8px;
	max-width: 100%;
	overflow: hidden;
`;

const SUserName = styled(UserName)`
	font-size: ${theme.fonts.tittelXXS.size};
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	flex: 1 1 auto;
	text-align: left;
`;

const SLikesContainer = styled.div`
	white-space: nowrap;
	vertical-align: middle;
	flex: 1 1 auto;
	text-align: right;
`;

const LIKE_ICON_HEIGHT = "21px;"

const SNumLikedThis = styled.div`
	display: inline-block;
	font-size: ${theme.fonts.tittelXXXS.size};
	height: ${LIKE_ICON_HEIGHT};
	margin-right: 8px;
`;

const SReviewMenu = styled(ReviewMenu)`
	position: absolute;
	top: 10px;
	right: 10px;
`;

const SLikeReviewIcon = styled(HeartSvg)`
	height: ${LIKE_ICON_HEIGHT};
	display: inline-block;
	vertical-align: middle;

	path {
		fill: ${theme.palette.turkis2};
	}
`;

const SLikeReviewButton = styled.button`
	background: none;
	border: none;
	padding: 0;
	margin: 0;
	cursor: pointer;
`;


//#endregion